import {
	CircleUserIcon,
	Divider,
	ExpandIcon,
	Flex,
	HistoryIcon,
	LinkExternalIcon,
	LogOutIcon,
	ModuleDocumentIcon,
	ModuleSalaryIcon,
	ModuleSurveyIcon,
	ModuleTipIcon,
	NavigationList,
	OpenModalIcon,
	ProductStaffIcon,
	Stack,
	Text,
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeSpaceVariable,
	TimeIcon,
	UndoIcon,
	UserGroupIcon,
	UserIcon,
} from "@caspeco/casper-ui-library";
import { useState } from "react";
import ImpersonateSelect from "../../components/impersonate-selector";
import { useTranslation } from "react-i18next";
import { MyPagesRoutes } from "../../types/routes";
import { NavButtonWithIcon } from "../../components/nav-button/nav-button";
import { useUserContext } from "../../hooks/use-user-context";
import config from "../../config/config";
import { useAppContext } from "../../hooks/use-app-context";
import { changeApplicationUrl, haveAppFunction, isApp, launchApp } from "../../utils/app-helper";
import { hasPermissionOnSystem } from "../../models/permission";
import PoweredByCaspeco from "../../assets/powered-by-caspeco";
import { analyticsPermssions, bookingPermissions, staffPermissions } from "../../types/permission-lists";
import CustomEventWrapper from "../../components/application-insights/custom-event-wrapper";
import { TrackEventNames } from "../../components/application-insights/track-event-name-enum";
import Page from "../../components/page";
import { isAndroid, isIOS } from "react-device-detect";

const NavRoot = () => {
	const { user, isSupport, isDeveloper, systems, permissions, logout, isImpersonating, canImpersonate } =
		useUserContext();
	const [showImpersonate, setShowImpersonate] = useState(isImpersonating);
	const { t } = useTranslation();
	const { version } = useAppContext();

	const handleClickAdmin = () => {
		if (haveAppFunction("launchApp")) {
			if (isAndroid) {
				launchApp(["caspecorms://", "https://play.google.com/store/apps/details?id=se.caspeco.caspecocloud"]);
			} else if (isIOS) {
				launchApp(["caspecorms://", "https://apps.apple.com/us/app/caspeco-cloud/id1305638475"]);
			}
		}
	};

	const analyticsPermission = systems.some((s) =>
		analyticsPermssions.some((p) => hasPermissionOnSystem(permissions, s.name, p)),
	);
	const staffPermission = systems.some((s) =>
		staffPermissions.some((p) => hasPermissionOnSystem(permissions, s.name, p)),
	);
	const bookingPermission = systems.some((s) =>
		bookingPermissions.some((p) => hasPermissionOnSystem(permissions, s.name, p)),
	);
	const adminPermission = analyticsPermission || staffPermission || bookingPermission;

	return (
		<Page title={"choice_menu"}>
			<Stack
				justifyContent={"space-between"}
				gap={ThemeSpaceVariable.Medium}
				height={"100%"}
				pb={ThemeSpaceVariable.Small}
			>
				<NavigationList>
					<Stack gap={ThemeSpaceVariable.Small}>
						<NavButtonWithIcon title={t("profile")} leftIcon={UserIcon} to={"/" + MyPagesRoutes.Profile} />
						<NavButtonWithIcon
							title={t("salary")}
							leftIcon={ModuleSalaryIcon}
							to={"/" + MyPagesRoutes.Salary}
							testId="nav-salary-link"
						/>
						<NavButtonWithIcon
							title={t("tip")}
							leftIcon={ModuleTipIcon}
							to={"/" + MyPagesRoutes.Tip}
							requiredPermission={["portal.views.tip"]}
						/>
						<NavButtonWithIcon
							title={t("documents")}
							leftIcon={ModuleDocumentIcon}
							to={"/" + MyPagesRoutes.Documents}
							testId="nav-documents-link"
						/>
						<NavButtonWithIcon
							title={t("period_overview")}
							leftIcon={HistoryIcon}
							to={"/" + MyPagesRoutes.PeriodOverview}
							testId="nav-period-overview-link"
						/>
						<NavButtonWithIcon
							title={t("clock")}
							leftIcon={TimeIcon}
							to={"/" + MyPagesRoutes.Clock}
							requiredPermission={["portal.clock.webclock"]}
						/>
						{adminPermission && isApp() && (
							<>
								<Divider
									orientation="horizontal"
									mt={ThemeSpaceVariable.Medium}
									mb={ThemeSpaceVariable.Medium}
								/>
								<CustomEventWrapper type="link" eventName={TrackEventNames.ToStaff}>
									<NavButtonWithIcon
										onClick={handleClickAdmin}
										title={t("nav_admin")}
										leftIcon={ProductStaffIcon}
										rightIcon={LinkExternalIcon}
									/>
								</CustomEventWrapper>
							</>
						)}
						<Divider orientation="horizontal" mt={ThemeSpaceVariable.Medium} mb={ThemeSpaceVariable.Medium} />
						{haveAppFunction("changeApplicationUrl") && (
							<NavButtonWithIcon
								onClick={() => changeApplicationUrl()}
								title={t("menu_return_to_old_mypages")}
								leftIcon={CircleUserIcon}
								rightIcon={UndoIcon}
							/>
						)}
						<NavButtonWithIcon
							to={"/" + MyPagesRoutes.Feedback}
							title={t("nav_feedback")}
							leftIcon={ModuleSurveyIcon}
							testId="nav-feedback-link"
						/>
						<NavButtonWithIcon
							onClick={logout}
							title={t("choice_logout")}
							leftIcon={LogOutIcon}
							rightIcon={null}
						/>
						{((user && canImpersonate) || isImpersonating) && (
							<>
								<NavButtonWithIcon
									onClick={() => {
										setShowImpersonate(!showImpersonate);
									}}
									title={t("impersonate")}
									leftIcon={UserGroupIcon}
									rightIcon={showImpersonate || isImpersonating ? ExpandIcon : OpenModalIcon}
								/>
								{(showImpersonate || isImpersonating) && <ImpersonateSelect />}
							</>
						)}
					</Stack>
				</NavigationList>
				<Stack alignItems={"center"} gap={ThemeSpaceVariable.XSmall}>
					<PoweredByCaspeco />
					<Flex
						w={"100%"}
						alignItems={"center"}
						color={ThemeColorVariable.OnBackgroundSubdued}
						gap={ThemeSpaceVariable.Small}
					>
						{(isSupport || isDeveloper) && config.commitHash && (
							<Text fontSize={ThemeFontSizeVariable.Small}>commit: {config.commitHash.substring(0, 7)}</Text>
						)}
						{(isSupport || isDeveloper) && config.releaseTag && (
							<Text fontSize={ThemeFontSizeVariable.Small}>tag: {config.releaseTag}</Text>
						)}
						<Text ml={"auto"} fontSize={ThemeFontSizeVariable.Small}>
							{version?.toString()}
						</Text>
					</Flex>
				</Stack>
			</Stack>
		</Page>
	);
};

export default NavRoot;
